import type { App } from "@teamworksdev/react";
import { z } from "zod";
import type { Grid } from "~/components";
import type { UserTypeCompact } from "../userTypes/types";

export type AppAccessCode = "limited" | "enabled";

export type AppAccessCondition = {
  team_document_id: string | null;
  user_type_document_id: string | null;
  hub_athlete_status_id: string | null;
};

export type EnabledApp = {
  app_id: number;
  name: string;
  slug: App;
  status: AppAccessCode;
};

export type AppAccessRule = {
  id: string;
  label: string;
  created_by: string | null;
  created_date: string | null;
  user_types: UserTypeCompact[];
  athlete_statuses?: AthleteStatus[];
  enabled_apps: EnabledApp[];
  updated_by: string | null;
  updated_date: string | null;
};

export type AppAccessRuleResult = {
  id: string;
  label: string;
  created_by: string | null;
  created_date: string | null;
  conditions: AppAccessCondition[];
  enabled_apps: Omit<EnabledApp, "name" | "slug">[];
  updated_by: string | null;
  updated_date: string | null;
};

export type AppAccessRuleTableRow = Omit<AppAccessRule, "user_types" | "enabled_apps"> & {
  enabled_apps: EnabledApp[];
  user_types: {
    [userTypeLabel: string]: {
      status: string;
      teamName: string;
    }[];
  };
  warnings: number;
};

export type AthleteStatus = {
  id: string;
  label: string;
};

export const DefaultAppRulesSortParams = {
  LabelAsc: "label.asc",
  LabelDesc: "label.desc",
} as const;

export type RuleCriteria = {
  athleteStatus?: string;
  isValidTeam: boolean;
  isValidUserType: boolean;
  teamName: string;
  userType: string;
};

// Don't need to included error messages since this is only used to generate
// a type for local storage.
const appRuleFormSchema = z.object({
  label: z.string().min(1),
  conditions: z
    .array(
      z.object({
        team_document_id: z.string().min(1),
        user_type_document_id: z.string().min(1),
        hub_athlete_status_id: z.string().nullable().optional(),
      }),
    )
    .min(1),
  enabled_apps: z.custom<Grid<AppAccessCode | "disabled">>(),
});

export type DefaultAppRuleFormValues = z.infer<typeof appRuleFormSchema>;

export const localStorageAppRuleSchema = z.object({
  date: z.string(),
  data: appRuleFormSchema,
});

export type LocalStorageAppRuleSchema = z.infer<typeof localStorageAppRuleSchema>;
